import { Box, BoxProps, Stack } from '@mui/material';
import { ComponentType, ReactNode } from 'react';

import { PresentationHoverRow } from '@/modules/presentation/components/PresentationHoverActions/PresentationHoverRow';
import { PRESENTATION_SPACING_UNITS } from '@/modules/presentation/presentation.constants';
import { getNumberedSlideTitle } from '@/modules/presentation/presentation.utils';
import { PresentationSlide } from '@/modules/presentation/PresentationSlide';
import { PresentationSlideProps } from '@/modules/presentation/PresentationSlide/PresentationSlide';
import { TrustSummaryData } from '@/modules/trusts/TrustDetails/TrustDetails.types';
import { TrustPoliciesTabPrint } from '@/modules/trusts/TrustPoliciesTab/TrustPoliciesTab';

const INSURANCE_POLICIES_SLIDE_TITLE = 'Insurance policies';

export interface EntityInsurancePoliciesSlideProps {
  pagePolicies: TrustSummaryData['insurancePolicies'];
  slideId: string;
  displayName: string;
  /** Entity kind shown under the entity name, matching other entity slides. */
  subheading: string;
  SlideWrapper?: ComponentType<BoxProps>;
  footer?: ReactNode;
  Slide?: ComponentType<PresentationSlideProps>;

  /**
   * Edit-mode floating toolbar for the design surface (supplied by the
   * presentation-aware wrapper). The policies table is wrapped in a hover
   * region only when this toolbar is present; the hover treatment is inert
   * outside the presentation hover provider, so the entity preview modal and
   * print paths (which also render this slide) are unaffected.
   */
  toolbar?: ReactNode;

  /**
   * When provided, the heading becomes the numbered "Insurance policies" title
   * (via {@link getNumberedSlideTitle}) with `displayName` as the subheading —
   * matching the multi-page PDF export. Omit for the default single heading
   * (entity name + `subheading`).
   */
  pageNumber?: { index: number; total: number };
}

export function EntityInsurancePoliciesSlide({
  pagePolicies,
  slideId,
  displayName,
  subheading,
  SlideWrapper = Box,
  footer,
  Slide = PresentationSlide.Slide,
  toolbar,
  pageNumber,
}: EntityInsurancePoliciesSlideProps) {
  const numberedHeading = pageNumber
    ? getNumberedSlideTitle(
        INSURANCE_POLICIES_SLIDE_TITLE,
        pageNumber.index,
        pageNumber.total
      )
    : null;

  return (
    <SlideWrapper>
      <Slide
        id={slideId}
        leftHeaderContent={
          <PresentationSlide.MainHeading
            heading={numberedHeading ?? displayName}
            subheading={numberedHeading ? displayName : subheading}
          />
        }
        footer={footer ?? <PresentationSlide.Footer />}
      >
        <Stack
          p={PRESENTATION_SPACING_UNITS}
          spacing={PRESENTATION_SPACING_UNITS}
          minHeight="90%"
        >
          <PresentationHoverRow
            id={`${slideId}-insurance-policies`}
            toolbar={toolbar}
            disabled={!toolbar}
            sx={{ flexGrow: 1 }}
          >
            <TrustPoliciesTabPrint insurancePolicies={pagePolicies} />
          </PresentationHoverRow>
        </Stack>
      </Slide>
    </SlideWrapper>
  );
}
